import { RefObject, useEffect, useRef, useState } from "react";

import { LearningMaterialSummary } from "shared/request/myHealthyAdvantageApi";
import { RoundedCard } from "shared/UI/RoundedCard/RoundedCard";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppReviewContext } from "core/state/AppReviewContext/AppReviewContext";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";

type UseLearningMaterialLineClampProps = {
  headingRef: RefObject<HTMLElement>;
  paragraphRef: RefObject<HTMLElement>;
  dependencies: any[];
};

export const useLearningMaterialLineClamp = ({ headingRef, paragraphRef, dependencies }: UseLearningMaterialLineClampProps) => {
  const [clampClass, setClampClass] = useState("line-clamp-2");

  const recalculateClampClass = () => {
    if (headingRef.current && paragraphRef.current) {
      const headingHeight = headingRef.current.offsetHeight;
      const paragraphHeight = paragraphRef.current.offsetHeight;

      const parentDiv = headingRef.current.parentElement!;
      const parentPaddingVertical =
        parseInt(window.getComputedStyle(parentDiv).paddingTop) + parseInt(window.getComputedStyle(parentDiv).paddingBottom);
      const availableSpace = parentDiv.clientHeight - parentPaddingVertical;

      if (headingHeight + paragraphHeight > availableSpace) {
        setClampClass("line-clamp-2");
      } else {
        setClampClass("line-clamp-3");
      }
    }
  };

  useEffect(() => {
    recalculateClampClass();

    window.addEventListener("resize", recalculateClampClass);

    return () => {
      window.removeEventListener("resize", recalculateClampClass);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, headingRef, paragraphRef]);

  const maxHeightClass = clampClass === "line-clamp-2" ? "max-h-10" : "max-h-16";
  return { clampClass, maxHeightClass };
};

type LearningMaterialCardProps = {
  item: LearningMaterialSummary;
  navigateBasePath: string;
};

export const LearningMaterialCard = ({ item, navigateBasePath }: LearningMaterialCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const { handlePositiveMoment } = useAppReviewContext();

  const { clampClass, maxHeightClass } = useLearningMaterialLineClamp({
    headingRef,
    paragraphRef,
    dependencies: [item.title, item.categories, item.duration],
  });

  const handleResourceClick = () => {
    trackEvents(EventNames.RESOURCE_CLICK, {
      value: item.title,
      type: item.articleType,
      categories: item.categories.map((category) => category.name).join(","),
    });
    navigate(`${navigateBasePath}${item.id}`);
    handlePositiveMoment();
  };

  return (
    <div className="mb-2">
      <button className="w-full text-left" onClick={handleResourceClick}>
        <RoundedCard className={""}>
          <div className="flex flex-row -m-5 h-[135px]">
            <div className={"w-1/3 overflow-hidden"}>
              <img
                className="object-cover object-center w-full h-full rounded-r-none rounded-card"
                src={`${item.image.url}?h=820`}
                alt={item.image.title ?? item.title}
              />
            </div>
            <div className="w-2/3 p-3">
              <h3 className="!p-0 mb-2 line-clamp-2" ref={headingRef}>
                {item.title}
              </h3>
              {/* set hardocded text size for description temporary till the smart filtering work is done */}
              <p className={`text-[14px] ${maxHeightClass}  ${clampClass} overflow-hidden`} ref={paragraphRef}>
                <span>
                  {item.categories
                    .map((c) => c.name)
                    .sort((a, b) => a.localeCompare(b))
                    .join(", ")}
                </span>
                {item.duration && (
                  <>
                    <span> | </span>
                    <span>{t("resources.durationMinutes", { duration: item.duration })}</span>
                  </>
                )}
              </p>
            </div>
          </div>
        </RoundedCard>
      </button>
    </div>
  );
};
